import {
  Mitarbeiter_Markierung,
  Mitarbeiter_RotationsplanVorlagenQuery,
  Mitarbeiter_RotationsplanVorlagenQueryVariables,
} from '@/gql/graphql';
import { useSuspenseQuery } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { ROTATIONSPLAN_VORLAGEN } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanVorlagen/graphql/queries';
import { getColorsForMarkierung } from '@/domain/mitarbeiter/features/rotationsplan/utils/getColorForMarkierung';
import { Markierung } from '@/domain/mitarbeiter/features/rotationsplan/entities/Markierung';

export type VorlagenArbeitsbereich = {
  id: string;
  name: string;
  color: string;
};

export type VorlagenAbschnitt = {
  id: string;
  name: string;
  arbeitsbereiche: VorlagenArbeitsbereich[];
};

export type RotationsplanVorlage = {
  id: string;
  titel: string;
  abschnitte: VorlagenAbschnitt[];
};

export type VorlagenBeruf = {
  id: string;
  name: string;
  kuerzel: string;
  color: string;
  vorlagen: RotationsplanVorlage[];
};

function toMarkierung(markierung: Mitarbeiter_Markierung): Markierung {
  return markierung as unknown as Markierung;
}

export function useRotationsplanVorlagen(): {
  vorlagenByBeruf: VorlagenBeruf[];
  error?: string;
} {
  const result = useSuspenseQuery<
    Mitarbeiter_RotationsplanVorlagenQuery,
    Mitarbeiter_RotationsplanVorlagenQueryVariables
  >(ROTATIONSPLAN_VORLAGEN);

  const vorlagenByBeruf = useMemo(() => {
    const groupedByBeruf = new Map<string, VorlagenBeruf>();

    for (const vorlage of result.data?.mitarbeiter_rotationsplanVorlagen ??
      []) {
      const berufId = vorlage.beruf.id;
      const existing = groupedByBeruf.get(berufId);

      const berufColor = getColorsForMarkierung(
        toMarkierung(vorlage.beruf.markierung),
      ).base;

      const abschnitte = [...vorlage.abschnitte]
        .sort((a, b) => a.position - b.position)
        .map((abschnitt) => {
          const bereicheById = new Map<string, VorlagenArbeitsbereich>();

          for (const station of abschnitt.stationen) {
            if (bereicheById.has(station.arbeitsbereich.id)) {
              continue;
            }

            bereicheById.set(station.arbeitsbereich.id, {
              id: station.arbeitsbereich.id,
              name: station.arbeitsbereich.name,
              color: getColorsForMarkierung(
                toMarkierung(station.arbeitsbereich.markierung),
              ).base,
            });
          }

          return {
            id: abschnitt.id,
            name: abschnitt.name,
            arbeitsbereiche: [...bereicheById.values()],
          };
        });

      const mappedVorlage: RotationsplanVorlage = {
        id: vorlage.id,
        titel: vorlage.titel,
        abschnitte,
      };

      if (!existing) {
        groupedByBeruf.set(berufId, {
          id: berufId,
          name: vorlage.beruf.name,
          kuerzel: vorlage.beruf.abkuerzung ?? '',
          color: berufColor,
          vorlagen: [mappedVorlage],
        });

        continue;
      }

      existing.vorlagen.push(mappedVorlage);
    }

    return [...groupedByBeruf.values()];
  }, [result.data]);

  if (result.error) {
    Sentry.captureException(result.error);
    return { vorlagenByBeruf: [] };
  }

  return { vorlagenByBeruf };
}
